import { Badge, cn } from "@devhop/ui";
import { GiftIcon } from "lucide-react";
import type { InstructorSessionByInstructorItemDTO } from "../api/client";

export function PayoutQuotaSummaryStrip({
  row,
  className,
}: {
  row: Pick<
    InstructorSessionByInstructorItemDTO,
    | "monthlyFreeGeneralClassSessions"
    | "retainerFreeGeneralClassSessions"
    | "billableGeneralClassLeadSessions"
  >;
  className?: string;
}) {
  const quota = row.monthlyFreeGeneralClassSessions ?? 0;
  if (quota <= 0) return null;

  const used = row.retainerFreeGeneralClassSessions ?? 0;
  const billable = row.billableGeneralClassLeadSessions ?? 0;
  const pct = Math.min(100, Math.round((used / quota) * 100));

  return (
    <div
      className={cn(
        "flex flex-col gap-2 rounded-lg border border-emerald-500/30 bg-gradient-to-r from-emerald-500/10 via-emerald-500/5 to-transparent px-4 py-3 sm:flex-row sm:items-center sm:justify-between",
        className,
      )}
    >
      <div className="flex items-center gap-2.5">
        <div className="flex size-8 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">
          <GiftIcon className="size-4" aria-hidden />
        </div>
        <div>
          <p className="font-medium text-sm">Session ຄລາສທົ່ວໄປຟຣີ (Retainer)</p>
          <p className="text-muted-foreground text-xs tabular-nums">
            Quota {quota.toLocaleString("lo-LA")} ຄັ້ງ/ເດືອນ
          </p>
        </div>
      </div>
      <div className="flex min-w-[200px] flex-1 flex-col gap-1.5 sm:max-w-xs">
        <div className="h-1.5 overflow-hidden rounded-full bg-emerald-950/10 dark:bg-emerald-950/40">
          <div
            className={cn(
              "h-full rounded-full",
              used >= quota ? "bg-amber-500" : "bg-emerald-500",
            )}
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="flex flex-wrap gap-2 text-xs tabular-nums">
          <Badge
            variant="outline"
            className="border-emerald-500/40 bg-background/80 text-[10px]"
          >
            ໃຊ້ {used}/{quota}
          </Badge>
          {billable > 0 ? (
            <Badge
              variant="outline"
              className="border-amber-500/40 bg-amber-500/10 text-[10px] text-amber-900 dark:text-amber-100"
            >
              +{billable} ຈ່າຍເພີ່ມ
            </Badge>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function PayoutQuotaRowHint({
  row,
  className,
}: {
  row: InstructorSessionByInstructorItemDTO;
  className?: string;
}) {
  const quota = row.monthlyFreeGeneralClassSessions ?? 0;
  if (quota <= 0) return null;

  const used = row.retainerFreeGeneralClassSessions ?? 0;
  const billable = row.billableGeneralClassLeadSessions ?? 0;
  const full = used >= quota;

  return (
    <div
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-[10px] tabular-nums",
        full
          ? "border-amber-500/35 bg-amber-500/10 text-amber-900 dark:text-amber-100"
          : "border-emerald-500/35 bg-emerald-500/10 text-emerald-800 dark:text-emerald-200",
        className,
      )}
      title={`Quota ${quota}/ເດືອນ · ຟຣີ ${used} · ຈ່າຍເພີ່ມ ${billable}`}
    >
      <GiftIcon className="size-3 shrink-0 opacity-70" aria-hidden />
      <span>
        {used}/{quota}
        {billable > 0 ? ` · +${billable}` : ""}
      </span>
    </div>
  );
}
